/**
 * /school-timetable — the page: a timetable you type in two minutes, a sheet
 * that is worth hanging on the wall, and a phone that knows what lesson is on.
 *
 * Modules:
 *   model.js / time.js / locale.js / subjects.js — the data and its rules
 *   layout.js + themes.js + icons.js — the sheet as a display list (mm)
 *   render-canvas.js — preview, PNG, wallpaper;  pdf.js — the vector PDF
 *   ics.js — calendar;  share.js — link and project file;  now.js — «now»
 *   editor.js — the controls on the left;  out.js — everything that leaves the page
 *
 * ⚠️ The timetable is a DOCUMENT the person builds, so it lives in the shared
 * draft store (tool-draft.js in the `work` storage): every change is written
 * within a second, a crash or a reload brings the last timetable back, and
 * «My timetables» lists them all. Nothing of the work goes to localStorage.
 * The texts never promise «saved forever»: it is this browser on this device,
 * plus the project file and the link as the doors out.
 */

import { newState, normalize, isBlank, fillSample, newId, PAPERS, weekIndex, usedSubjects } from './model.js';
import { conv, dayNames, fmtTime, fmtRange } from './locale.js';
import { composePaper, layoutPage } from './layout.js';
import { drawPaper, makeMeasure } from './render-canvas.js';
import { themeOf } from './themes.js';
import { decodeLink } from './share.js';
import { nowStatus } from './now.js';
import { bindEditor, renderEditor } from './editor.js';
import { bindOutputs, renderNow } from './out.js';
import { fill } from './i18n.js';
import * as TM from './telemetry.js';

const CFG = window.__STT_CFG || {};
const I18N = CFG.i18n || {};
const SLUG = 'school-timetable';
const LOCALE = window.__LOCALE || CFG.locale || 'en';
const $ = (id) => document.getElementById(id);
const t = (k) => (I18N[k] != null ? I18N[k] : k);
/** A phrase with its values: every `:name` by whole name, in one pass (i18n.js). */
const f = (k, vars) => fill(t(k), vars);

/* ── the app context every module works through ───────────────────────── */
const A = {
    CFG, LOCALE, SLUG, t, f, $,
    S: null,
    ui: { day: null, week: 0, sel: null, open: null, drag: null },
    bg: { file: null, img: null, url: '' },
    measure: null,
    fontsReady: false,
    lastPaper: null,
    hist: { stack: [], ptr: -1, group: '', at: 0 },
};
window.__sttApp = A;   // opora for the stand: state visible without reading pixels

/* ── telemetry (never the timetable itself: only its shape) — telemetry.js ── */
A.stage = TM.stageOnce;
A.ae = TM.ae;
A.funnel = TM.funnel;
A.diag = TM.diag;
A.caught = TM.caught;
A.critical = TM.critical;
A.shapeOf = TM.shapeOf;
/** The person got a file: once per (way, this very timetable), with the timetable's shape. */
A.complete = (via, extra) => TM.complete(via + ':' + TM.hashOf(JSON.stringify(A.S) + (A.bg.file ? A.bg.file.size : '')),
    Object.assign({ via }, A.shape(), A.warnCounts(), extra || {}));
A.toast = (msg, type) => { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) { /* no toast layer */ } };
A.shape = () => ({
    days: A.S.days.length, periods: A.S.periods, weeks: A.S.weeks, mode: A.S.mode,
    theme: A.S.look.theme, layout: A.S.look.layout, paper: A.S.look.paper, lessons: lessonCount(A.S),
    bells: A.S.bells && A.S.bells.on ? 1 : 0, locale: A.S.locale,
});
/* lessons with a subject (a cell can hold only a room or a teacher) */
function lessonCount(S) {
    return Object.values(S.cells).filter((c) => c && (c.s || (c.alt && c.alt.s))).length;
}
/** How many cells of the drawn sheet did not fit (cut / teacher dropped / short name): the «can't» of the sheet. */
A.warnCounts = () => {
    const w = (A.lastPaper && A.lastPaper.parts[0] && A.lastPaper.parts[0].lay.warn) || [];
    const n = (k) => new Set(w.filter((x) => x.kind === k).map((x) => x.d + ':' + x.n + ':' + (x.week == null ? '' : x.week))).size;
    return { cut: n('cut'), drop: n('drop'), short: n('short') };
};

/* ── labels the sheet prints ──────────────────────────────────────────── */
A.labels = () => {
    const S = A.S;
    const uni = S.mode === 'uni';
    const num = S.weekNames === 'num';
    return {
        head: uni ? t('head_default_uni') : t('head_default'),
        room: t('sheet_room'), roomUni: t('sheet_room_uni'), bld: t('sheet_bld'), place: t('sheet_place'),
        // a bare room number with the country's word (ko «301호») — the labels speak the page language
        roomNo: conv(LOCALE).roomNo || '',
        gap: t('sheet_gap'), bells: t('sheet_bells'),
        weekShortA: num ? t('week_num_short_a') : t('week_ab_short_a'),
        weekShortB: num ? t('week_num_short_b') : t('week_ab_short_b'),
        weekA: num ? t('week_num_a') : t('week_ab_a'),
        weekB: num ? t('week_num_b') : t('week_ab_b'),
        kinds: { lec: t('kind_lec'), sem: t('kind_sem'), lab: t('kind_lab'), pr: t('kind_pr') },
        teacher: t('field_teacher'),
    };
};

/*
 * Shortcut names as this keyboard prints them: «Ctrl+Z» (de «Strg+Z», a key of the
 * page), «⌘Z» and «⇧⌘Z» on a Mac. The tooltip of ↶↷, the line under the list and the
 * hint after copying a day say the same thing. A phone without a keyboard hears
 * only about the ↶ button. Chrome on a Mac names its platform «macOS», Safari «MacIntel»
 * (an iPad too): the match ignores case.
 */
const MAC = (() => { try { return /mac|iphone|ipad|ipod/i.test((navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || ''); } catch (_) { return false; } })();
const TOUCH_ONLY = (() => { try { return matchMedia('(hover: none) and (pointer: coarse)').matches; } catch (_) { return false; } })();
A.keys = () => ({
    undo: MAC ? '\u2318Z' : t('key_ctrl') + '+Z',
    redo: MAC ? '\u21e7\u2318Z' : t('key_ctrl') + '+Y',
    alt: MAC ? '\u2325' : t('key_alt'),
});
A.undoTip = () => (TOUCH_ONLY ? t('undo_tip_touch') : f('undo_tip', { keys: A.keys().undo }));
function labelShortcuts() {
    const k = A.keys();
    if ($('sttUndo')) $('sttUndo').title = f('undo_title', { keys: k.undo });
    if ($('sttRedo')) $('sttRedo').title = f('redo_title', { keys: k.redo });
    if ($('sttHint')) $('sttHint').textContent = f('keys_hint', { alt: k.alt });
}

A.fmtTime = (m) => fmtTime(m, A.S.locale, { h12: A.S.bells.h12 });
A.fmtRange = (a, b) => fmtRange(a, b, A.S.locale, { h12: A.S.bells.h12 });
A.days = () => dayNames(LOCALE);

A.env = (extra) => Object.assign({
    measure: A.measure,
    labels: A.labels(),
    dayNames: A.days(),
    fmtTime: A.fmtTime,
    fmtRange: A.fmtRange,
    rtl: conv(LOCALE).rtl,
    bg: !!(A.bg.img && A.S.look.bg),
    kind: 'sheet',
}, extra || {});

A.paperMm = () => PAPERS[A.S.look.paper] || PAPERS.A4;
A.compose = (extra) => composePaper(A.S, A.env(extra), A.paperMm());
A.layoutWall = (w, h, opts) => layoutPage(A.S, A.env(Object.assign({ pageW: 100, pageH: 100 * h / w }, opts)));

/* ── fonts: the same files as the PDF ─────────────────────────────────── */
let STACK = { r: '', b: '' };
A.fontCss = (face, bold) => {
    const fam = '"PTF ' + face + (bold ? ' Bold' : '') + '"';
    return fam + (STACK[bold ? 'b' : 'r'] ? ', ' + STACK[bold ? 'b' : 'r'] : '') + ', sans-serif';
};

function allText() {
    const S = A.S;
    const out = [S.title.head, S.title.cls, S.title.who, S.title.year, A.labels().head];
    Object.values(S.cells).forEach((c) => {
        out.push(c.s || '', c.room || '', c.teacher || '', c.note || '', c.bld || '');
        if (c.alt) out.push(c.alt.s || '', c.alt.room || '', c.alt.teacher || '', c.alt.note || '');
    });
    usedSubjects(S).forEach((x) => out.push(x.short || ''));
    const dn = A.days();
    out.push(dn.long.join(' '), dn.short.join(' '));
    return out.join(' ');
}

let fontGen = 0;
A.ensureFonts = async () => {
    const TF = window.PdfTextFont;
    const gen = ++fontGen;
    if (!TF) {
        /* the font helper did not arrive (both addresses): the sheet is drawn in the system face rather
           than not at all — before 15.10.2026 the preview stayed empty and every button did nothing */
        if (!A.fontsMissing) { A.fontsMissing = true; A.critical('stt_asset_failed', { asset: 'pdf-text-font' }); }
        A.measure = makeMeasure(A.fontCss);
        A.fontsReady = true;
        A.fontsDone = (A.fontsDone || 0) + 1;
        A.renderPreview();
        return;
    }
    try {
        await TF.load();
        if (!STACK.r) STACK = { r: TF.cssStack(false), b: TF.cssStack(true) };
        const th = themeOf(A.S.look.theme);
        const faces = new Set([th.faces.head, th.faces.body, th.faces.meta, 'noto']);
        if (A.S.look.font && A.S.look.font !== 'auto') faces.add(A.S.look.font);
        const text = allText();
        const jobs = [];
        faces.forEach((f) => [false, true].forEach((b) => {
            const fam = TF.cssFamily(f, b);
            if (fam && document.fonts && document.fonts.load) jobs.push(document.fonts.load((b ? '700' : '400') + ' 20px "' + fam + '"', 'AaЯя1').catch(() => {}));
        }));
        jobs.push(TF.cssPrepare(text, true), TF.cssPrepare(text, false));
        await Promise.race([Promise.all(jobs), new Promise((r) => setTimeout(r, 6000))]);
    } catch (e) {
        console.warn('[school-timetable] fonts:', e);
        A.critical('stt_asset_failed', { asset: 'fonts' });
    }
    if (gen !== fontGen) return;
    A.measure = makeMeasure(A.fontCss);
    A.fontsReady = true;
    A.fontsDone = (A.fontsDone || 0) + 1;   // the stand waits on this after a theme change
    A.renderPreview();
};

/* ── history (undo / redo) ────────────────────────────────────────────── */
function snap() { return JSON.stringify(A.S); }
A.histReset = () => { A.hist = { stack: [snap()], ptr: 0, group: '', at: 0 }; A.syncUndo(); };
A.syncUndo = () => {
    const u = $('sttUndo');
    const r = $('sttRedo');
    if (u) u.disabled = A.hist.ptr <= 0;
    if (r) r.disabled = A.hist.ptr >= A.hist.stack.length - 1;
};

/**
 * Every edit ends here. `group` merges a burst of typing in one field into one
 * undo step; `opts.editor` re-renders the controls (a structural change), and
 * the preview and the draft always follow.
 */
A.commit = (group, opts = {}) => {
    const now = Date.now();
    const s = snap();
    const H = A.hist;
    if (group && group === H.group && now - H.at < 1200 && H.ptr === H.stack.length - 1 && H.ptr > 0) {
        H.stack[H.ptr] = s;
    } else {
        H.stack = H.stack.slice(0, H.ptr + 1);
        H.stack.push(s);
        if (H.stack.length > 120) H.stack.shift();
        H.ptr = H.stack.length - 1;
    }
    H.group = group || '';
    H.at = now;
    A.syncUndo();
    if (opts.editor) renderEditor(A);
    A.renderPreview();
    renderNow(A);
    A.draftTouch();
    A.stage('first_edit', A.shape());
    if (lessonCount(A.S) >= 5) A.stage('filled', A.shape());
    if (opts.fonts) A.ensureFonts();
};

function restore(i) {
    const H = A.hist;
    if (i < 0 || i >= H.stack.length) return;
    H.ptr = i;
    H.group = '';
    A.S = normalize(JSON.parse(H.stack[i]), LOCALE);
    A.syncUndo();
    renderEditor(A);
    A.renderPreview();
    A.draftTouch();
}
A.undo = () => { restore(A.hist.ptr - 1); A.ae('undo'); };
A.redo = () => { restore(A.hist.ptr + 1); A.ae('redo'); };

/* ── drafts: «My timetables» in this browser ──────────────────────────── */
const DRAFT = { store: null, id: null, live: false, rows: [] };
A.DRAFT = DRAFT;
const DRAFT_AGE_MS = 400 * 864e5;   // a timetable made in September must still be here in May

A.draftStore = () => {
    if (DRAFT.store !== null) return DRAFT.store;
    DRAFT.store = false;
    try {
        if (window.__toolDraft) {
            // No count cap (was 30): a timetable is a few kilobytes, and the 31st must not
            // silently take the oldest with it (18.10.2026, report #9299 on the shared store).
            DRAFT.store = window.__toolDraft.store(SLUG, { maxAgeMs: DRAFT_AGE_MS, debounceMs: 600, onConflict: draftForked });
        }
    } catch (_) { /* no IndexedDB here: the page still works, the link and the file still save */ }
    return DRAFT.store;
};

A.labelOf = (S) => {
    const parts = [S.name, S.title.cls, S.title.who].map((x) => String(x || '').trim()).filter(Boolean);
    return parts.length ? parts.join(' · ') : '';
};

function draftPayload() {
    const S = A.S;
    return {
        v: 1,
        state: JSON.parse(JSON.stringify(S)),
        files: A.bg.file && S.look.bg ? [A.bg.file] : [],
        label: A.labelOf(S),
        preview: A.thumb || '',
        meta: { editedAt: Date.now(), lessons: Object.keys(S.cells).length, theme: S.look.theme, days: S.days.length },
    };
}

function draftSaved(r) {
    const el = $('sttSaveState');
    if (!r) return;
    if (r.ok) {
        if (el) { el.textContent = t('draft_saved'); el.hidden = false; }
        if (r.degraded) A.toast(t('draft_no_room'), 'warning');
    } else if (r.why !== 'foreign') {
        if (el) { el.textContent = t('draft_save_failed'); el.hidden = false; }
        A.ae('draft_save_failed', { why: String(r.why || '').slice(0, 30) });
    }
}

function draftForked() {
    const S = A.draftStore();
    if (!S) return;
    A.S.id = newId();
    DRAFT.id = A.S.id;
    S.claim(DRAFT.id);
    S.schedule(DRAFT.id, draftPayload, draftSaved);
    A.toast(t('draft_forked'), 'info');
    A.ae('draft_forked');
}

A.draftTouch = () => {
    if (!DRAFT.live) return;
    const St = A.draftStore();
    if (!St) return;
    if (!DRAFT.id) {
        DRAFT.id = A.S.id;
        St.claim(DRAFT.id);
    }
    St.schedule(DRAFT.id, draftPayload, draftSaved);
};
A.draftFlush = () => { const St = A.draftStore(); return St ? St.flush() : Promise.resolve(); };

A.refreshDrafts = async () => {
    const St = A.draftStore();
    if (!St) return [];
    try {
        const [rows, busy] = await Promise.all([St.list(), St.claimedElsewhere()]);
        DRAFT.rows = rows;
        DRAFT.busy = busy;
    } catch (_) { DRAFT.rows = []; }
    return DRAFT.rows;
};

/** Put a timetable on the page (from a draft, a link, a file, «new»). */
A.adopt = async (state, opts = {}) => {
    const St = A.draftStore();
    if (St && DRAFT.id && DRAFT.id !== state.id) { try { await St.flush(); St.release(DRAFT.id); } catch (_) { /* nothing to release */ } }
    A.S = normalize(state, LOCALE);
    DRAFT.id = opts.unsaved ? null : A.S.id;
    if (St && DRAFT.id) {
        const ok = await St.claim(DRAFT.id).catch(() => true);
        if (!ok) draftForked();
    }
    A.ui.day = A.S.days[0];
    A.ui.week = 0;
    A.ui.sel = null;
    A.ui.open = null;
    await A.setBgFile(opts.bgFile || null, { quiet: true });
    A.histReset();
    renderEditor(A);
    renderNow(A);
    A.ensureFonts();
    if (!opts.unsaved && St) St.schedule(DRAFT.id, draftPayload, draftSaved);
};

A.openDraft = async (id) => {
    const St = A.draftStore();
    if (!St) return false;
    await St.flush().catch(() => {});
    const d = await St.load(id);
    if (!d) { A.toast(t('draft_open_failed'), 'error'); return false; }
    const busy = (await St.claimedElsewhere().catch(() => ({})))[id];
    const state = d.state;
    if (busy) state.id = newId();
    A.thumb = d.preview || '';   // the list keeps its picture until the sheet is drawn again
    await A.adopt(state, { bgFile: d.files && d.files[0] ? d.files[0] : null });
    if (busy) A.toast(t('draft_forked'), 'info');
    A.funnel('draft_open', { age_d: Math.round((d.ageMs || 0) / 864e5) });
    return true;
};

A.newTimetable = async (mode) => {
    A.thumb = '';
    await A.adopt(newState(LOCALE, { mode: mode || 'school' }), { unsaved: true });
    A.funnel('new', { mode: mode || 'school' });
};

A.fillSample = () => {
    fillSample(A.S);
    A.commit('sample', { editor: true, fonts: true });
    A.funnel('sample', { locale: A.S.locale });
};

/* ── background photo ─────────────────────────────────────────────────── */
A.setBgFile = async (file, opts = {}) => {
    if (A.bg.url) { try { URL.revokeObjectURL(A.bg.url); } catch (_) { /* already gone */ } }
    A.bg = { file: null, img: null, url: '' };
    if (!file) { if (!opts.quiet) { A.S.look.bg = false; A.commit('bg', { editor: true }); } return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = 'async';
    const ok = await new Promise((res) => { img.onload = () => res(true); img.onerror = () => res(false); img.src = url; });
    if (!ok) { URL.revokeObjectURL(url); if (!opts.quiet) A.toast(t('bg_failed'), 'error'); return; }
    A.bg = { file, img, url };
    if (!opts.quiet) { A.S.look.bg = true; A.commit('bg', { editor: true }); }
    else A.renderPreview();
};

/* ── preview ──────────────────────────────────────────────────────────── */
let raf = 0;
A.renderPreview = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(drawPreview);
};

function drawPreview() {
    const cv = $('sttCanvas');
    if (!cv || !A.S || !A.measure) return;
    const box = $('sttPreview');
    const paper = A.compose(A.S.weeks === 2 && A.S.look.abSheets ? { week: A.ui.week } : {});
    A.lastPaper = paper;
    const cw = Math.max(200, box.clientWidth);
    const maxH = Math.max(320, Math.min(window.innerHeight * 0.78, 900));
    const cssScale = Math.min(cw / paper.w, maxH / paper.h);
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const images = A.bg.img ? { bg: A.bg.img } : {};
    drawPaper(cv, paper, { scale: cssScale * dpr, font: A.fontCss, images, overlay: overlay });
    cv.style.width = Math.round(paper.w * cssScale) + 'px';
    cv.style.height = Math.round(paper.h * cssScale) + 'px';
    A.cssScale = cssScale;
    cv.setAttribute('aria-label', f('preview_label', { n: Object.keys(A.S.cells).length }));
    renderWarnings(paper.parts[0].lay.warn);
    // a small picture for «My timetables»
    clearTimeout(A._thumbT);
    A._thumbT = setTimeout(() => {
        try {
            const c = document.createElement('canvas');
            const k = 150 / Math.max(cv.width, cv.height);
            c.width = Math.round(cv.width * k);
            c.height = Math.round(cv.height * k);
            c.getContext('2d').drawImage(cv, 0, 0, c.width, c.height);
            A.thumb = c.toDataURL('image/jpeg', 0.7);
        } catch (_) { A.thumb = ''; }
    }, 400);
    clearTimeout(A._themesT);
    A._themesT = setTimeout(drawThemeThumbs, 500);
    if (A.renderWall) A.renderWall();
    A.stage('ready', A.shape());
}

/** Every theme button shows THIS timetable in that theme, not a stock picture. */
function drawThemeThumbs() {
    if (!A.measure) return;
    const S = A.S;
    document.querySelectorAll('#sttThemes [data-theme]').forEach((b) => {
        const cv = b.querySelector('canvas');
        if (!cv) return;
        const look = Object.assign({}, S.look, { theme: b.getAttribute('data-theme'), twoUp: false, bg: false, bw: false });
        const st = Object.assign({}, S, { look });
        try {
            const paper = composePaper(st, A.env({ bg: false }), A.paperMm());
            const k = 150 / Math.max(paper.w, paper.h);
            drawPaper(cv, paper, { scale: k, font: A.fontCss, images: {} });
        } catch (e) { /* a thumbnail never stops the page */ }
    });
}

/** Screen-only marks: today's column, the selected lesson, the drop target, the lesson on now. */
function overlay(g, part, k) {
    const hits = part.lay.hits;
    const S = A.S;
    const now = new Date();
    const today = now.getDay();
    const st = S.bells && S.bells.on ? nowStatus(S, now) : null;
    g.save();
    hits.forEach((h) => {
        if (h.kind === 'day' && h.d === today && S.days.indexOf(today) >= 0) {
            g.strokeStyle = '#f59e0b';
            g.lineWidth = Math.max(2, 0.6 * k);
            g.setLineDash([]);
            g.strokeRect(h.x * k - 1, h.y * k - 1, h.w * k + 2, h.h * k + 2);
        }
        if (h.kind === 'cell' && st && st.kind === 'lesson' && st.cur && h.d === today && h.n === st.cur.n && (h.week == null || h.week === st.week)) {
            g.strokeStyle = '#f59e0b';
            g.lineWidth = Math.max(2, 0.7 * k);
            g.strokeRect(h.x * k, h.y * k, h.w * k, h.h * k);
        }
    });
    const sel = A.ui.sel;
    if (sel) {
        hits.forEach((h) => {
            if (h.kind === 'cell' && h.d === sel.d && h.n === sel.n && (h.week == null || h.week === (A.ui.week || 0))) {
                g.strokeStyle = '#2563eb';
                g.lineWidth = Math.max(2, 0.55 * k);
                g.setLineDash([]);
                g.strokeRect(h.x * k + 1, h.y * k + 1, h.w * k - 2, h.h * k - 2);
            }
        });
    }
    const dr = A.ui.drag;
    if (dr && dr.over) {
        g.strokeStyle = '#16a34a';
        g.lineWidth = Math.max(2, 0.8 * k);
        g.setLineDash([4, 3]);
        g.strokeRect(dr.over.x * k, dr.over.y * k, dr.over.w * k, dr.over.h * k);
    }
    g.restore();
}

function renderWarnings(warn) {
    const el = $('sttWarn');
    if (!el) return;
    const short = new Set();
    const cut = new Set();
    const drop = new Set();
    (warn || []).forEach((w) => {
        const k = w.d + ':' + w.n + ':' + (w.week == null ? '' : w.week);
        if (w.kind === 'short') short.add(k);
        else if (w.kind === 'cut') cut.add(k);
        else if (w.kind === 'drop') drop.add(k);
    });
    const parts = [];
    if (cut.size) parts.push(f('warn_cut', { n: cut.size }));
    if (drop.size) parts.push(f('warn_drop', { n: drop.size }));
    if (short.size) parts.push(f('warn_short', { n: short.size }));
    el.hidden = !parts.length;
    el.textContent = parts.length ? parts.join(' ') + ' ' + t('warn_fix') : '';
}

/* ── the canvas is also a way to pick and move lessons ────────────────── */
function hitAt(ev) {
    const cv = $('sttCanvas');
    const r = cv.getBoundingClientRect();
    const x = (ev.clientX - r.left) / A.cssScale;
    const y = (ev.clientY - r.top) / A.cssScale;
    const part = A.lastPaper && A.lastPaper.parts[0];
    if (!part) return null;
    const hits = part.lay.hits;
    for (let i = hits.length - 1; i >= 0; i--) {
        const h = hits[i];
        if (x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h) return h;
    }
    return null;
}

function bindCanvas() {
    const cv = $('sttCanvas');
    let down = null;
    cv.addEventListener('pointerdown', (e) => {
        const h = hitAt(e);
        if (!h || h.kind !== 'cell') return;
        down = { h, x: e.clientX, y: e.clientY, moved: false };
        try { cv.setPointerCapture(e.pointerId); } catch (_) { /* old browser */ }
    });
    cv.addEventListener('pointermove', (e) => {
        if (!down) { const h = hitAt(e); cv.style.cursor = h && h.kind === 'cell' ? 'pointer' : 'default'; return; }
        if (!down.moved && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 6) return;
        down.moved = true;
        const h = hitAt(e);
        A.ui.drag = { from: down.h, over: h && h.kind === 'cell' && !(h.d === down.h.d && h.n === down.h.n) ? h : null };
        cv.style.cursor = 'grabbing';
        A.renderPreview();
    });
    const end = (e) => {
        if (!down) return;
        const d0 = down;
        down = null;
        const drag = A.ui.drag;
        A.ui.drag = null;
        if (d0.moved && drag && drag.over) {
            A.moveLesson({ d: d0.h.d, n: d0.h.n }, { d: drag.over.d, n: drag.over.n }, 'drag');
            return;
        }
        if (!d0.moved && e.type === 'pointerup') A.selectCell(d0.h.d, d0.h.n, d0.h.week, { focus: true });
        A.renderPreview();
    };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);
}

/* ── boot ─────────────────────────────────────────────────────────────── */
async function boot() {
    if (!$('sttApp')) return;
    // the fallback measure until the real faces are in: the page is usable at once
    A.measure = null;
    bindEditor(A);
    bindOutputs(A);
    bindCanvas();
    labelShortcuts();
    window.addEventListener('resize', () => A.renderPreview());
    document.addEventListener('keydown', (e) => {
        if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
        const tag = (document.activeElement && document.activeElement.tagName) || '';
        // a list («copy the day to…») has no text to undo: Ctrl+Z right after choosing in it undoes the copy, as the line under the list says
        const typing = /INPUT|TEXTAREA/.test(tag) && document.activeElement.type !== 'checkbox';
        if (typing || !$('sttApp').contains(document.activeElement) && document.activeElement !== document.body) return;
        if (e.key === 'z' || e.key === 'Z') { e.preventDefault(); if (e.shiftKey) A.redo(); else A.undo(); }
        else if (e.key === 'y' || e.key === 'Y') { e.preventDefault(); A.redo(); }
    });

    // 1. a link carries a whole timetable: it opens as a new one of this person's
    let fromLink = null;
    if (/(^|[#&])s=/.test(location.hash)) {
        const hashLen = location.hash.length;
        const LZ = await A.lz().catch(() => null);
        if (!LZ) {
            /* the link is fine — the page could not fetch its reader: keep the hash, so «Reload» opens it */
            A.bootFailed('lz-string');
        } else {
            fromLink = decodeLink(location.hash, LZ, LOCALE);
            try { history.replaceState(null, '', location.pathname + location.search); } catch (_) { /* keep the hash */ }
            if (!fromLink) { A.toast(t('link_broken'), 'error'); A.diag('link_broken', { len: hashLen }); }
        }
    }

    const St = A.draftStore();
    DRAFT.live = true;
    if (fromLink) {
        fromLink.id = newId();
        await A.adopt(fromLink);
        A.notice(t('link_opened'), false);
        A.funnel('link_opened', A.shape());
    } else if (St) {
        let lost = [];
        try { lost = await St.takeLost(); } catch (_) { lost = []; }
        const rows = await A.refreshDrafts();
        if (rows.length) {
            await A.openDraft(rows[0].id);
            A.ae('draft_reopened', { n: rows.length });
        } else {
            await A.newTimetable();
        }
        if (lost.length) { A.notice(t('draft_lost'), false); A.ae('draft_lost', { n: lost.length }); }
    } else {
        await A.newTimetable();
    }

    renderNow(A);
    setInterval(() => { renderNow(A); A.renderPreview(); }, 20000);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') { renderNow(A); A.renderPreview(); } });
    /* the link's packer is small and is fetched ahead: «Link» must reach the clipboard inside the tap
       itself — Safari refuses a copy that waited for a download (NotAllowedError on every iPhone) */
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    idle(() => { A.lz().catch(() => {}); });
}

/** LZString (for links) arrives on demand: our origin first, the other site second. */
let lzLoad = null;
A.lz = async () => {
    if (window.LZString) return window.LZString;
    if (!lzLoad) {
        const urls = (CFG.assets && CFG.assets.lz) || ['/vendor/lz-string/lz-string.min.js'];
        lzLoad = ((window.__assetChain && window.__assetChain.loadScript)
            ? window.__assetChain.loadScript(urls, 'lz-string')
            : new Promise((res, rej) => { const sc = document.createElement('script'); sc.src = urls[0]; sc.onload = res; sc.onerror = rej; document.head.appendChild(sc); }))
            .catch((e) => { lzLoad = null; A.critical('stt_asset_failed', { asset: 'lz-string' }); throw e; });
    }
    await lzLoad;
    return window.LZString || null;
};

/** A part of the tool did not arrive: the plate of the blade with «Reload page» (it keeps the link in the address). */
A.bootFailed = (why) => {
    const box = $('sttBoot');
    if (box) box.hidden = false;
    A.critical('tool_boot_failed', { why: String(why || '').slice(0, 30) });
};

/** A line of notice above the editor, with an optional «start over» action. */
A.notice = (text, withNew) => {
    const el = $('sttNotice');
    if (!el) return;
    $('sttNoticeText').textContent = text;
    $('sttNoticeNew').hidden = !withNew;
    el.hidden = false;
};

A.isBlank = () => isBlank(A.S);
A.weekIndex = (d) => weekIndex(A.S, d);

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
